import React, { FC, useEffect, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { Users, Play, Plus, Lock, RefreshCw, NotebookTabs } from 'lucide-react';
import { playSE } from './SoundEffect';
import { CharaSelect } from './CharaSelect';

const SERVER_URL = "https://bad-assort.com";

export type roomsType = {
  id: string,
  name: string,
  players: number,
  playerNames: string[],
  hasPassword: boolean
}[];

interface ChildProps {
  socket: Socket | null;
  setErrorState: React.Dispatch<React.SetStateAction<string | null>>;
  playerName: string;
  setPlayerName: React.Dispatch<React.SetStateAction<string>>;
  icon: string;
  setIcon: React.Dispatch<React.SetStateAction<string>>;
}

export const GameLobby: FC<ChildProps> = ({ socket, setErrorState, playerName, setPlayerName, icon, setIcon }) => {
  const [roomId, setRoomId] = useState<number | null>(null);
  const [roomName, setRoomName] = useState<string>('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tipsNum, setTipsNum] = useState<number>(0);

  const [selectedRoom, setSelectedRoom] = useState<number | null>(null);
  const [displayIconChangeModal, setDisplayIconChangeModal] = useState<boolean>(false);

  const [rooms, setRooms] = useState<roomsType>([]);

  const defNameRef = useRef<HTMLInputElement | null>(null);

  const tips: string[] = [
    "パスワードを設定するとプライベートルームになります。",
    "ルールに関する問い合わせには一切回答しません。",
    "ネタバレは特に禁止しておりません。",
    "放棄したマッチへの再入室はできません。",
    "長時間放置すると接続切れになります。リロードしてください。",
    "名前が金色のプレイヤーは開発者アカウントです。",
  ];

  const joinLobby = () => {
    const defName = defNameRef.current?.value;
    if (defName) {
      if (defName.length < 2) {
        setErrorState("名前が短すぎます。");
      } else if (defName.length > 9) {
        setErrorState("名前が長すぎます。");
      } else {
        setPlayerName(defName);
        playSE("cardput1");
      }
    } else {
      setErrorState("名前を入力してください。");
    }
  }

  const fetchRooms = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${SERVER_URL}/api-yaminoge/rooms`);
      if (response.ok) {
        const data = await response.json();
        setRooms(data);
      } else {
        console.error('Failed to fetch rooms');
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRoom = () => {
    if (!roomName.trim()) {
      setErrorState('ルーム名を入力してください');
      return;
    }
    if (roomName.length < 2) {
      setErrorState('ルーム名が短すぎます');
      return;
    }
    if (roomName.length > 12) {
      setErrorState('ルーム名が長すぎます');
      return;
    }
    setRoomId(null);
    console.log('Create new room:', roomName);
    socket?.emit('join_room', {
      ID: null,
      roomName: roomName,
      password: password,
      dispName: playerName,
      icon: icon,
    });
  };

  const handleJoinRoom = (id: number, name?: string) => {
    console.log('Joining room:', roomName);
    socket?.emit('join_room', {
      ID: id,
      roomName: name ?? `ルーム${id}`,
      password: password,
      dispName: playerName,
      icon: icon,
    });
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // クリックされた要素が背景自体の場合のみ閉じる
    if (e.target === e.currentTarget) {
      setSelectedRoom(null);
      setDisplayIconChangeModal(false);
    }
  };

  useEffect(() => {
    fetchRooms();
    const interval = setInterval(fetchRooms, 10000);
    const tp = setInterval(() => { setTipsNum(prev => { return (prev + 1) % tips.length }) }, 10000);
    return () => { clearInterval(interval); clearInterval(tp) };
  }, []);

  if (!playerName) {
    return (
      <div className="w-full h-full rounded-xl bg-[radial-gradient(80%_80%_at_50%_50%,#fcf5f9_0%,#c1d2e6_100%)] p-8 grid place-items-center">

        <div className="w-[70em] max-w-[calc(100vw-88px)] flex flex-col gap-4 items-center bg-[#fcfcf8]/15 backdrop-blur-md rounded-2xl p-6 border border-[#fcfcf8]/40">
          <div className='flex flex-col justify-center items-center md:flex-row md:my-8 md:gap-8'>
            <img src={`${process.env.PUBLIC_URL}/img/logo.webp`}
              className='max-w-[60%] mb-4 object-contain' alt='yaminoge-logo'></img>
            <div className="flex flex-col justify-center items-center gap-3">
              <input
                ref={defNameRef}
                type="text"
                placeholder="あなたのなまえ"
                className="w-[16em] max-w-full text-center px-3 py-1 md:px-4 rounded-lg bg-[#9cacff]/25 text-[#1a1514] border border-[#9cacff]/60 text-base placeholder-gray-400 focus:outline-none focus:border-[#9cacff]/80 focus:bg-[#9cacff]/45"
              />
              <p>好きなアイコンをえらんでね</p>
              <CharaSelect setIcon={setIcon}></CharaSelect>
            </div>
          </div>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => alert("There is no darkness, only ignorance.\n\n暗闇は無く、無知があるのみ。")}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-br from-[#0575E6] to-[#021B79] text-white font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base hover:bg-gradient-to-br hover:from-[#1585f6] hover:to-[#122b89] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(212,165,116,0.3)]"
            >
              <NotebookTabs size={20} />
              ルール
            </button>
            <button
              onClick={joinLobby}
              className="flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-br from-[#0575E6] to-[#021B79] text-white font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base hover:bg-gradient-to-br hover:from-[#1585f6] hover:to-[#122b89] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(212,165,116,0.3)]"
            >
              <Play size={20} />
              入場
            </button>
          </div>
          <p className='text-xs'>(C) 2025 Bad Assort</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full h-full rounded-xl bg-[radial-gradient(80%_80%_at_50%_50%,#fcf5f9_0%,#c1d2e6_100%)] p-4 md:p-8 flex justify-center items-start md:items-center">
        <div className="w-full max-w-4xl max-h-full mx-auto flex flex-col gap-4 overflow-y-auto">
          <div className="relative flex justify-center items-center">
            <img src={`${process.env.PUBLIC_URL}/img/logo.webp`} alt='yaminoge-logo' className='max-w-[60%]'></img>
            <button
              onClick={() => {
                setDisplayIconChangeModal(true);
                playSE("cardput1");
              }}
              className="absolute bottom-0 right-0 flex items-center justify-center px-4 py-2 bg-gradient-to-br from-[#0575E6] to-[#021B79] text-white text-sm rounded-lg border-none cursor-pointer transition-all duration-300 text-base hover:bg-gradient-to-br hover:from-[#1585f6] hover:to-[#122b89] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(212,165,116,0.3)]"
            >
              アイコン変更
            </button>
          </div>

          <div className='flex-1 max-w-[70em] grid grid-cols-1 md:grid-cols-2 gap-2'>
            <div className="max-w-[70em] bg-slate-400/25 backdrop-blur-md rounded-2xl p-6 border border-slate-400/50">
              <h2 className="text-2xl font-semibold text-slate-700 mb-4">ルーム作成</h2>
              <div className="w-full max-w-[40em] mx-auto flex flex-col gap-4">
                <div className="flex justify-center gap-3">
                  <input
                    type="text"
                    placeholder="ルーム名"
                    value={roomName}
                    onChange={(e) => setRoomName(e.target.value)}
                    className="flex-1 max-w-[20em] px-3 py-2 md:px-4 md:py-3 rounded-lg bg-slate-400/20 text-[#1a1514] border border-slate-400/50 text-base placeholder-slate-700/50 focus:outline-none focus:border-slate-400 focus:bg-slate-400/50"
                    autoComplete="off"
                  />
                </div>
                <div className="flex justify-center gap-3">
                  <input
                    type="text"
                    placeholder="ひみつの合言葉(任意)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="flex-1 max-w-[20em] px-3 py-2 md:px-4 md:py-3 rounded-lg bg-slate-400/20 text-[#1a1514] border border-slate-400/50 text-base placeholder-slate-700/50 focus:outline-none focus:border-slate-400 focus:bg-slate-400/50"
                    autoComplete="off"
                  />
                </div>
                <p className='text-sm text-slate-700/50'>個人情報などの重要な情報を合言葉にしないでください。</p>
                <div className="flex justify-center">
                  <button
                    onClick={handleCreateRoom}
                    className="w-[10em] flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-br from-[#0575E6] to-[#021B79] text-white font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base hover:bg-gradient-to-br hover:from-[#1585f6] hover:to-[#122b89] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(212,165,116,0.3)]"
                  >
                    <Play size={20} />
                    入場
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-slate-400/25 backdrop-blur-md rounded-2xl p-6 border border-slate-400/50">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-slate-700">ルーム参加</h2>
                <button
                  onClick={fetchRooms}
                  className="bg-gradient-to-br from-[#0575E6] to-[#021B79] text-white border-none rounded-lg p-2 cursor-pointer transition-all duration-300 flex items-center justify-center hover:bg-gradient-to-br hover:from-[#1585f6] hover:to-[#122b89] hover:shadow-[0_4px_12px_rgba(212,165,116,0.3) hover:rotate-90"
                  disabled={isLoading}
                >
                  <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                </button>
              </div>

              <div className="flex flex-col gap-3 overflow-x-clip overflow-y-scroll max-h-80">
                {isLoading && rooms.length === 0 ? (
                  <div className="text-center py-12 px-4 text-slate-700 text-base">
                    読み込み中...
                  </div>
                ) : rooms.length === 0 ? (
                  <div className="text-center text-sm py-12 px-4 text-slate-700 text-base">
                    現在ルームはありません。<br />
                    新しいルームを作成してください。
                  </div>
                ) : (
                  rooms.map((room) => (
                    <div
                      key={room.id}
                      className="bg-violet-400/20 rounded-lg p-4 border border-violet-400/50 cursor-pointer transition-all duration-300 flex items-center justify-between hover:bg-violet-400/5 hover:border-violet-400/80 hover:translate-x-1"
                      onClick={() => {
                        if (room.hasPassword) {
                          setSelectedRoom(Number(room.id));
                        } else if (playerName.trim()) {
                          handleJoinRoom(Number(room.id));
                        }
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-violet-400/20 p-3 rounded-lg">
                          <Users size={24} className="text-white" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <p className="text-violet-900 font-semibold text-lg m-0">{room.name}</p>
                          <div className="flex flex-col gap-0.5">
                            <p className="text-violet-900 text-sm m-0">
                              ID: {room.id}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {room.hasPassword && <Lock size={20} className="text-yellow-600" />}
                        <span className={`px-4 py-2 rounded-lg font-medium ${room.players === 2
                          ? 'bg-rose-600/30 text-rose-600'
                          : 'bg-green-500/70 text-white'
                          }`}>
                          {room.players === 2 ? '満室' : '参加可能'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <p className='text-sm text-slate-700/50' key={tipsNum}>{tips[tipsNum]}</p>
        </div>
      </div>

      {selectedRoom !== null &&
        <div className="absolute w-full h-full inset-0 bg-slate-600/50 p-8 grid place-items-center" onClick={handleBackdropClick}>
          <div className="max-w-4xl mx-auto flex flex-col gap-4">

            <div className='flex-1 max-w-[70em] flex justify-center'>
              <div className="max-w-[70em] bg-slate-100 backdrop-blur-md rounded-2xl p-6 border border-slate-400">
                <h2 className="text-xl font-semibold text-slate-700 mb-4">ひみつの合言葉を入力してください</h2>
                <div className="w-full max-w-[40em] mx-auto flex flex-col gap-4">
                  <div className="flex justify-center gap-3">
                    <input
                      type="password"
                      placeholder="ひみつの合言葉"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="flex-1 max-w-[20em] px-3 py-2 md:px-4 md:py-3 rounded-lg bg-slate-400/20 text-[#1a1514] border border-slate-400/50 text-base placeholder-slate-700/50 focus:outline-none focus:border-slate-400 focus:bg-slate-400/50"
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex justify-center">
                    <button
                      onClick={handleCreateRoom}
                      className="w-[10em] flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-br from-[#0575E6] to-[#021B79] text-white font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base hover:bg-gradient-to-br hover:from-[#1585f6] hover:to-[#122b89] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(212,165,116,0.3)]"
                    >
                      <Play size={20} />
                      入場
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      {displayIconChangeModal &&
        <div className="absolute w-full h-full inset-0 bg-slate-600/50 p-8 grid place-items-center" onClick={handleBackdropClick}>
          <div className="max-w-4xl mx-auto flex flex-col gap-4">

            <div className='flex-1 max-w-[70em] flex justify-center'>
              <div className="max-w-[70em] bg-slate-100 backdrop-blur-md rounded-2xl p-6 border border-slate-400">
                <div className="w-full max-w-[40em] mx-auto flex flex-col gap-4">
                  <CharaSelect setIcon={setIcon}></CharaSelect>
                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        setDisplayIconChangeModal(false);
                        playSE("cardput1");
                      }}
                      className="w-[8em] flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-br from-[#0575E6] to-[#021B79] text-white font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 text-base hover:bg-gradient-to-br hover:from-[#1585f6] hover:to-[#122b89] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(212,165,116,0.3)]"
                    >
                      決定
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </>
  );
}
